import React from "react";
import {
  Box,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Button,
  Divider,
  useColorModeValue,
  Flex,
  Badge,
  Icon,
} from "@chakra-ui/react";

const CustomDataCard = ({
  // Title props
  title = "",
  titleColor = "gray.800",
  titleSize = "lg",
  titleWeight = "bold",

  // Subtitle props
  subtitle,
  subtitleColor = "gray.600",
  subtitleSize = "sm",
  subtitleWeight = "normal",

  // Data props
  data = [],
  keyWidth = "120px",
  keyColor = "gray.700",
  keySize = "sm",
  keyWeight = "medium",
  valueColor = "gray.600",
  valueSize = "sm",
  valueWeight = "normal",
  valueAlign = "right",

  // Button props
  showButton = false,
  buttonText = "View Details",
  buttonVariant = "outline",
  buttonColorScheme = "blue",
  buttonSize = "sm",
  onButtonClick,
  buttonPosition = "bottom", // "bottom" or "header"

  // Card styling props
  cardBg,
  borderColor,
  borderWidth = "1px",
  borderRadius = "lg",
  shadow = "md",
  padding = 6,
  spacing = 3,

  // Header styling
  headerIcon,
  headerBg,
  headerPadding = 0,

  // Additional props
  maxItems,
  showDividers = true,
  hoverEffect = false,

  ...props
}) => {
  // Color mode values
  const defaultCardBg = useColorModeValue("white", "gray.800");
  const defaultBorderColor = useColorModeValue("gray.200", "gray.600");
  const defaultTitleColor = useColorModeValue("gray.800", "white");
  const defaultSubtitleColor = useColorModeValue("gray.600", "gray.400");
  const defaultKeyColor = useColorModeValue("gray.700", "gray.300");
  const defaultValueColor = useColorModeValue("gray.600", "gray.400");
  const dividerColor = useColorModeValue("gray.100", "gray.700");

  // Filter and limit data if needed
  const displayData = maxItems ? data.slice(0, maxItems) : data;
  console.log("displayData ===>",displayData);
  

  // Clean empty values
  const cleanValue = (value) => {
    if (
      !value ||
      value.toString().trim() === "" ||
      value.toString().trim().match(/^\s+$/)
    ) {
      return "Not provided";
    }
    return value.toString().trim();
  };

  return (
    <Card
      bg={cardBg || defaultCardBg}
      shadow={shadow}
      borderRadius={borderRadius}
      border={borderWidth}
      borderColor={borderColor || defaultBorderColor}
      overflow="hidden"
      maxW={"400px"}
      // maxWidth={"400px"}
      _hover={
        hoverEffect
          ? {
              shadow: "lg",
              transform: "translateY(-2px)",
              transition: "all 0.3s ease",
            }
          : {}
      }
      {...props}
    >
      <CardBody p={padding}>
        <VStack spacing={spacing} align="stretch">
          {/* Header Section */}
          <Box
            bg={headerBg}
            p={headerPadding}
            mx={headerPadding ? -padding : 0}
            mt={headerPadding ? -padding : 0}
          >
            <Flex
              justify="space-between"
              align="flex-start"
              p={headerPadding ? padding : 0}
              gap={4}
            >
              {/* Title and Subtitle */}
              {title && (
                <VStack align="flex-start" spacing={1} flex={1}>
                  <HStack spacing={2} align="center">
                    {headerIcon && (
                      <Icon as={headerIcon} color="blue.500" boxSize={5} />
                    )}
                    <Text
                      fontSize={titleSize}
                      fontWeight={titleWeight}
                      color={titleColor || defaultTitleColor}
                      // noOfLines={1}
                    >
                      {title}
                    </Text>
                  </HStack>

                  {subtitle && (
                    <Text
                      fontSize={subtitleSize}
                      fontWeight={subtitleWeight}
                      color={subtitleColor || defaultSubtitleColor}
                      noOfLines={2}
                    >
                      {subtitle}
                    </Text>
                  )}
                </VStack>
              )}

              {/* Header Button */}
              {showButton && buttonPosition === "header" && (
                <Button
                  size={buttonSize}
                  variant={buttonVariant}
                  colorScheme={buttonColorScheme}
                  onClick={onButtonClick}
                  flexShrink={0}
                >
                  {buttonText}
                </Button>
              )}
            </Flex>
          </Box>

          {/* Data Section */}
          {displayData.length > 0 && (
            <VStack spacing={0} align="stretch">
              {displayData.map((item, index) => {
                const key = item.key || item.label || item.name;
                const value = item.value || item.data || item.text;
                const cleanedValue = cleanValue(value);

                return (
                  <Box key={index}>
                    <HStack spacing={4} py={2} align="flex-start">
                      {/* Key (Left Side) */}
                      <Text
                        fontSize={item.keySize || keySize}
                        fontWeight={item.keyWeight || keyWeight}
                        color={item.keyColor || keyColor || defaultKeyColor}
                        minW={keyWidth}
                        flexShrink={0}
                        noOfLines={1}
                      >
                        {key} :
                      </Text>

                      {/* Value (Right Side) */}
                      <Flex
                        flex={1}
                        justify={valueAlign}
                        align="center"
                        gap={2}
                      >
                        <Text
                          fontSize={item.valueSize || valueSize}
                          fontWeight={item.valueWeight || valueWeight}
                          color={
                            item.valueColor || valueColor || defaultValueColor
                          }
                          textAlign={valueAlign}
                          flex={1}
                          noOfLines={item.maxLines || 2}
                          opacity={cleanedValue === "Not provided" ? 0.6 : 1}
                          fontStyle={
                            cleanedValue === "Not provided"
                              ? "italic"
                              : "normal"
                          }
                        >
                          {cleanedValue}
                        </Text>

                        {/* Badge */}
                        {item.badge && (
                          <Badge
                            colorScheme={item.badgeColorScheme || "blue"}
                            variant={item.badgeVariant || "solid"}
                            size="sm"
                          >
                            {item.badge}
                          </Badge>
                        )}

                        {/* Icon */}
                        {item.icon && (
                          <Icon
                            as={item.icon}
                            color={item.iconColor || "gray.400"}
                            boxSize={item.iconSize || 4}
                          />
                        )}
                      </Flex>
                    </HStack>

                    {/* Divider */}
                    {showDividers && index < displayData.length - 1 && (
                      <Divider borderColor={dividerColor} />
                    )}
                  </Box>
                );
              })}
            </VStack>
          )}

          {/* Bottom Button */}
          {showButton && buttonPosition === "bottom" && (
            <>
              {displayData.length > 0 && <Divider borderColor={dividerColor} />}
              <Flex justify="center">
                <Button
                  size={buttonSize}
                  variant={buttonVariant}
                  colorScheme={buttonColorScheme}
                  onClick={onButtonClick}
                >
                  {buttonText}
                </Button>
              </Flex>
            </>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

export default CustomDataCard;
